import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Table,
  Button,
  Input,
  Space,
  Tag,
  Modal,
  message,
  Pagination,
  Select,
  Collapse,
} from "antd";
import {
  SearchOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import useAdminData from "../hooks/useAdminData";

const AdminRiders = () => {
  const { bikers, loading, error, fetchBikers, approveBiker, rejectBiker } =
    useAdminData();

  const userProfile = useSelector((state) => state.userProfile?.user);
  const userRole = userProfile?.role?.toUpperCase() || "";
  const isAdminOrSE = ["ADMIN", "SUPPORT_EXECUTIVE"].includes(userRole);

  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
    { value: "suspended", label: "Suspended" },
  ];

  const typeOptions = [
    { value: "", label: "All Types" },
    { value: "KONNECT", label: "KONNECT" },
    { value: "RESTAURANT", label: "RESTAURANT" },
    { value: "THIRD_PARTY", label: "THIRD_PARTY" },
  ];

  useEffect(() => {
    fetchBikers({
      page: currentPage,
      limit: pageSize,
      search: searchText,
      status: statusFilter,
      bikerType: typeFilter,
    });
  }, [
    currentPage,
    pageSize,
    searchText,
    statusFilter,
    typeFilter,
    fetchBikers,
  ]);

  const handleApprove = async (bikerId, bikerName) => {
    Modal.confirm({
      title: "Approve Rider",
      content: `Are you sure you want to approve "${bikerName}"? This will set the rider status to "approved".`,
      onOk: async () => {
        try {
          await approveBiker(bikerId);
          message.success("Rider approved successfully");
        } catch (error) {
          message.error("Failed to approve rider");
        }
      },
    });
  };

  const handleReject = async (bikerId, bikerName) => {
    Modal.confirm({
      title: "Reject Rider",
      content: `Are you sure you want to reject "${bikerName}"? This will set the rider status to "rejected".`,
      onOk: async () => {
        try {
          await rejectBiker(bikerId);
          message.success("Rider rejected successfully");
        } catch (error) {
          message.error("Failed to reject rider");
        }
      },
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setCurrentPage(1);
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  const handleTypeFilter = (value) => {
    setTypeFilter(value);
    setCurrentPage(1);
  };

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div>
      <div className="mb-4 flex gap-4">
        <Input
          placeholder="Search riders..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ width: 250 }}
        />
        <Select
          placeholder="Filter by status"
          value={statusFilter}
          onChange={handleStatusFilter}
          options={statusOptions}
          style={{ width: 150 }}
        />
        <Select
          placeholder="Filter by type"
          value={typeFilter}
          onChange={handleTypeFilter}
          options={typeOptions}
          style={{ width: 150 }}
        />
      </div>

      <div className="mobile topup-data">
        <Collapse defaultActiveKey={[]}>
          {bikers?.data &&
            bikers.data.length > 0 &&
            bikers.data.map((biker, index) => (
              <Collapse.Panel
                showArrow={false}
                key={index}
                header={
                  <p style={{ display: "inline" }}>
                    {biker.user?.firstName || ""} {biker.user?.lastName || ""} -{" "}
                    <Tag
                      color={
                        biker.status === "approved"
                          ? "green"
                          : biker.status === "pending"
                            ? "orange"
                            : "red"
                      }
                    >
                      {biker.status?.toUpperCase()}
                    </Tag>
                    <Tag color="blue">{biker.bikerType}</Tag>
                  </p>
                }
              >
                <div>
                  <ul className="mobile-list-flex">
                    <li className="flex gap-4">
                      <span>Name:</span>
                      <span className="text-right">
                        {biker.user?.firstName || ""}{" "}
                        {biker.user?.lastName || ""}
                      </span>
                    </li>
                    <li className="flex gap-4">
                      <span>Email:</span>
                      <span>{biker.user?.email || "N/A"}</span>
                    </li>
                    <li className="flex gap-4">
                      <span>Phone:</span>
                      <span>
                        {biker.user?.phoneCode}
                        {biker.user?.phoneNumber || "N/A"}
                      </span>
                    </li>
                    <li className="flex gap-4">
                      <span>Type:</span>
                      <span>{biker.bikerType || "N/A"}</span>
                    </li>
                    <li className="flex gap-4">
                      <span>Experience:</span>
                      <span>
                        {biker.experience ? `${biker.experience} years` : "N/A"}
                      </span>
                    </li>
                    <li className="flex gap-4">
                      <span>Location:</span>
                      <span>{biker.localArea || "N/A"}</span>
                    </li>
                    <li className="flex gap-4">
                      <span>Status:</span>
                      <span>
                        <Tag
                          color={
                            biker.status === "approved"
                              ? "green"
                              : biker.status === "pending"
                                ? "orange"
                                : "red"
                          }
                        >
                          {biker.status?.toUpperCase()}
                        </Tag>
                      </span>
                    </li>
                    <li className="flex gap-4">
                      <span>Created At:</span>
                      <span>
                        {new Date(biker.createdAt).toLocaleDateString()}
                      </span>
                    </li>
                    <li className="flex gap-4">
                      <span>Actions:</span>
                      <span>
                        {isAdminOrSE ? (
                          <Space>
                            {biker.status === "pending" && (
                              <>
                                <Button
                                  type="primary"
                                  icon={<CheckOutlined />}
                                  onClick={() =>
                                    handleApprove(
                                      biker.id,
                                      `${biker.user?.firstName} ${biker.user?.lastName}`,
                                    )
                                  }
                                  size="small"
                                >
                                  Approve
                                </Button>
                                <Button
                                  danger
                                  icon={<CloseOutlined />}
                                  onClick={() =>
                                    handleReject(
                                      biker.id,
                                      `${biker.user?.firstName} ${biker.user?.lastName}`,
                                    )
                                  }
                                  size="small"
                                >
                                  Reject
                                </Button>
                              </>
                            )}
                          </Space>
                        ) : (
                          <Tag>{biker.status?.toUpperCase()}</Tag>
                        )}
                      </span>
                    </li>
                  </ul>
                </div>
              </Collapse.Panel>
            ))}
        </Collapse>
        <div style={{ marginTop: "2%" }}></div>
        <div className="mt-4 flex justify-end">
          <Pagination
            current={currentPage}
            pageSize={pageSize}
            total={bikers?.meta?.total || 0}
            onChange={handlePageChange}
            showSizeChanger={false}
          />
        </div>
      </div>
    </div>
  );
};

export default AdminRiders;
